import { useEffect, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UsersFour, Plus, Trash, Crown, PencilSimple, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_META = {
  owner: { label: "Owner", icon: Crown, cls: "bg-amber-500/15 text-amber-700" },
  manager: { label: "Manager", icon: UsersFour, cls: "bg-secondary/15 text-secondary" },
  editor: { label: "Editor", icon: PencilSimple, cls: "bg-primary/10 text-primary" },
};

export default function InstitutionTeam({ instId }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/institutions/${instId}/team`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [instId]);

  if (data === null || data === false) return null;
  const canManage = data.can_manage;

  const invite = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/institutions/${instId}/team/invite`, { email: email.trim(), role });
      setData((d) => ({ ...d, team: res.team }));
      toast.success(res.status === "invited" ? "Invitation email sent" : "Team member added");
      setEmail("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const changeRole = async (m, newRole) => {
    try { const { data: res } = await api.patch(`/institutions/${instId}/team/role`, { email: m.email, role: newRole }); setData((d) => ({ ...d, team: res.team })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email}?`)) return;
    try { const { data: res } = await api.delete(`/institutions/${instId}/team/${encodeURIComponent(m.email)}`); setData((d) => ({ ...d, team: res.team })); toast.success("Removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <section className="mt-10 rounded-2xl border border-border/60 bg-card p-6" data-testid="institution-team">
      <div className="flex items-center gap-2">
        <span className="inline-grid h-10 w-10 place-items-center rounded-xl bg-accent text-primary"><UsersFour size={20} weight="duotone" /></span>
        <div>
          <h2 className="font-heading text-xl font-medium">Team &amp; access</h2>
          <p className="text-sm text-muted-foreground">People who help manage this page — with Manager or Editor access.</p>
        </div>
      </div>

      {canManage && (
        <div className="mt-5 grid gap-3 rounded-xl border border-border/60 bg-muted/20 p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end" data-testid="team-invite-form">
          <div className="space-y-1.5">
            <Label>Invite a colleague by email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@myschool.org" data-testid="team-invite-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-32" data-testid="team-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="editor">Editor</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={invite} disabled={busy} className="rounded-full gap-1.5" data-testid="team-invite-submit"><Plus size={15} /> Invite</Button>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {data.team.map((m) => {
          const rm = ROLE_META[m.role] || ROLE_META.editor;
          const RoleIcon = rm.icon;
          return (
            <div key={m.email || m.user_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border/60 p-3" data-testid="team-member">
              <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-accent text-sm font-medium text-primary">
                {m.avatar_file_id ? <img src={fileUrl(m.avatar_file_id)} alt="" className="h-full w-full object-cover" /> : (m.name || m.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{m.name || m.email || "Member"}</p>
                {m.email && <p className="truncate text-xs text-muted-foreground">{m.email}</p>}
              </div>
              {m.status === "invited" && <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"><Clock size={12} /> Invited</span>}
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${rm.cls}`}><RoleIcon size={12} weight="fill" /> {rm.label}</span>
              {canManage && m.role !== "owner" && (
                <div className="flex items-center gap-1.5">
                  <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                    <SelectTrigger className="h-8 w-28 text-xs" data-testid="team-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="editor">Editor</SelectItem></SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => remove(m)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid="team-remove"><Trash size={15} /></Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {canManage && <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><EnvelopeSimple size={13} /> Managers can invite others and edit content. Editors can edit content only.</p>}
    </section>
  );
}
